<template>
    <form id="stripe-payment-form" class="min-h-[340px]">
        <div v-if="!isElementReady && !elementError" class="flex flex-col gap-4 w-full">
            <MiscSkeleton :repeat="5" text size="large" />
        </div>
        <div v-else-if="elementError" class="p-4 bg-error/10 text-error rounded-xs">
            {{ elementError }}
        </div>
        <div id="stripe-payment-element">
            <!--Stripe.js injects the Payment Element-->
        </div>
    </form>
</template>

<script setup lang="ts">
import { loadStripe } from '@stripe/stripe-js'
import type { Schemas } from '#shopware'

const { sessionContext } = useSessionContext()
const { cart } = useCart()
const { user } = useUser()
const { stripe, elements, configs, options, isElementReady } = useStripe()
const order = inject<Ref<Schemas['Order'] | null>>('orderData', ref(null))
const elementError = ref<string | null>(null)

interface StripeCreateElementsResponse {
    element?: {
        theme?: string
        mode: string
        amount: number
        currency: string
        layout: string
    }
    noZipcodeCollect?: boolean
}

interface StripePaymentElementOptions {
    defaultValues: {
        billingDetails: {
            address: {
                country?: string
                postal_code?: string
            }
            email?: string
            name?: string
        }
    }
    layout?: object
    fields?: {
        billingDetails: {
            address: {
                postalCode: string
            }
        }
    }
}

const layouts: Record<string, object> = {
    'tabs': {
        type: 'tabs',
        defaultCollapsed: false
    },
    'accordion': {
        type: 'accordion',
        defaultCollapsed: false,
        radios: false,
        spacedAccordionItems: true,
        visibleAccordionItemsCount: 0
    },
    'radio': {
        type: 'accordion',
        defaultCollapsed: false,
        radios: true,
        spacedAccordionItems: true,
        visibleAccordionItemsCount: 0
    }
}

const appearance: Record<string, unknown> = {
    rules: {
        '.AccordionItem': {
            borderColor: '#fff',
            boxShadow: 'false',
        },
        '.AccordionItem--selected': {
            borderColor: '#fff',
            boxShadow: 'false',
        },
    }
}

const scramble = 'ePiRtSeRaWpOhS';
const shopId = options.value.appShopId;
const tmp1 = shopId.match(/.{1,2}/g);
const tmp2 = scramble.match(/.{1,1}/g);
let tmp3 = '';
let j = 0;
let x = 0;
for (let i=0; i<10; i++) {
    if(tmp1?.[i] !== undefined) {
        tmp3 += tmp1[i];
    } else {
        tmp3 += tmp1?.[j];
        j++;
    }
    if(tmp2?.[i] !== undefined) {
        tmp3 += tmp2[i];
    } else {
        tmp3 += tmp2?.[x];
        x++;
    }
}
const initToken = tmp3;
let authToken: { token?: string } | undefined;

onMounted(async () => {
    isElementReady.value = false
    elementError.value = null
    await initialize()
})

onBeforeUnmount(() => {
    isElementReady.value = false
    elementError.value = null
})

async function initialize () {
    try {
        await getShopConfigs();

        if (configs.value.paymentElements) {
            stripe.value = await loadStripe(configs.value.key)

            if (!stripe.value) {
                throw new Error('Failed to load Stripe')
            }

            const createElements: StripeCreateElementsResponse = await fetch( (options.value.appUrl + "/create-elements"), {
                method: "POST",
                headers: {"Content-Type": "application/json"},
                body: JSON.stringify({
                    shopId: options.value.appShopId,
                    currencyIso: sessionContext.value?.currency?.isoCode,
                    amount: order.value?.amountTotal != null ? order.value.amountTotal : cart.value?.price?.totalPrice,
                    appVersion: options.value.appVersion
                }),
            }).then((r) => r.json());

            if (!createElements?.element) {
                throw new Error('Failed to create Stripe elements configuration')
            }

            appearance.theme = createElements.element.theme ? createElements.element.theme : (typeof appearance.theme !== 'undefined' ? appearance.theme : "stripe");

            const opts = {
                mode: createElements.element.mode,
                amount: createElements.element.amount,
                capture_method: 'automatic',
                currency: createElements.element.currency.toLowerCase(),
                appearance: appearance,
                paymentMethodCreation: 'manual',
                locale: options.value.locale
            };

            elements.value = stripe?.value?.elements(opts);

            const layout = createElements.element.layout;

            const elementOptions: StripePaymentElementOptions = {
                defaultValues: {
                    billingDetails: {
                        address: {
                            country: sessionContext.value?.shippingLocation?.country?.iso,
                            postal_code: user.value?.activeShippingAddress?.zipcode
                        },
                        email: user.value?.email,
                        name: `${user.value?.firstName} ${user.value?.lastName}`
                    }
                },
                layout: layouts[layout]
            };

            if (createElements.noZipcodeCollect) {
                elementOptions['fields'] = {
                    billingDetails: {
                        address: {
                            postalCode: 'never'
                        }
                    }
                };
            }

            const paymentElement = elements?.value?.create("payment", elementOptions);
            await paymentElement?.mount("#stripe-payment-element");

            isElementReady.value = true
        }
    } catch (error: unknown) {
        console.error('Stripe initialization error:', error)
        elementError.value = error instanceof Error ? error.message : 'Failed to load payment element. Please try again or select a different payment method.'
        isElementReady.value = false
    }
}

async function getShopConfigs() {
    authToken = await fetch( (options.value.appUrl + "/auth/token"), {
        method: "POST",
        headers: {"Content-Type": "application/json"},
        body: JSON.stringify({
            shopId: options.value.appShopId,
            token: initToken,
            appVersion: options.value.appVersion
        }),
    }).then((r) => r.json());

    configs.value = await fetch( (options.value.appUrl + "/shop/configuration"), {
        method: "POST",
        headers: {"Content-Type": "application/json"},
        body: JSON.stringify({
            shopId: options.value.appShopId,
            salesChannelId: sessionContext.value?.salesChannel?.id,
            token: authToken?.token,
            appVersion: options.value.appVersion
        }),
    }).then((r) => r.json());
}
</script>
